/**
 * Notifications Library
 *
 * Helper functions for sending push notifications.
 * Handles visibility checks and calls the send-push Edge Function.
 */

import { getSupabase, guestAuthHeaders } from './supabase';
import i18n from '@/i18n';

interface SendPushOptions {
  userId: string;
  userType: 'landlord' | 'guest';
  bookingId?: string;
  title: string;
  body: string;
  url?: string;
  tag?: string;
  notificationType?: 'guest_action' | 'new_message' | 'support_reply' | 'checkin_reminders' | 'checkout_reminders';
  data?: Record<string, unknown>;
}

// Deduplication: track recently sent notifications to prevent duplicates
const recentlySent = new Map<string, number>();
const DEDUP_WINDOW_MS = 5000; // 5 seconds

/**
 * Send a push notification via Edge Function
 * Includes client-side deduplication to prevent duplicate sends
 */
export async function sendPushNotification(options: SendPushOptions): Promise<boolean> {
  // Dedup key: the same notification sent twice in a row (tag or userId+type)
  // — including the text, because chat pushes share one tag per conversation
  // and a second, different message within 5 s used to be dropped silently.
  const dedupKey = `${options.tag || `${options.userId}-${options.notificationType}-${options.bookingId}`}|${options.body}`;
  const now = Date.now();
  const lastSent = recentlySent.get(dedupKey);

  if (lastSent && now - lastSent < DEDUP_WINDOW_MS) {
    if (import.meta.env.DEV) console.debug('Skipping duplicate push notification:', dedupKey);
    return true; // Already sent recently
  }

  recentlySent.set(dedupKey, now);

  // Clean up old entries
  if (recentlySent.size > 50) {
    for (const [key, time] of recentlySent) {
      if (now - time > DEDUP_WINDOW_MS) recentlySent.delete(key);
    }
  }

  try {
    const { error } = await getSupabase().functions.invoke('send-push', {
      body: options,
      // In the guest portal the guest proves who they are with the link's token
      // (send-push only lets a guest notify the host of their own booking).
      headers: guestAuthHeaders(),
    });

    if (error) {
      console.error('Failed to send push notification:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error sending push notification:', err);
    return false;
  }
}

/**
 * Check if push should be sent based on Page Visibility API
 * Returns true if the user is NOT actively viewing the relevant page
 */
export function shouldSendPushLocally(
  notificationType: 'new_message' | 'guest_action',
  currentBookingId?: string,
  targetBookingId?: string
): boolean {
  // If document is hidden (user on different tab/app), send push
  if (document.hidden) {
    return true;
  }

  // Check current page
  const currentPath = window.location.pathname;

  switch (notificationType) {
    case 'new_message':
      // Don't send if LANDLORD is viewing messages for the same booking
      if (currentPath.includes('/messages') || currentPath.includes('/chat')) {
        if (currentBookingId && targetBookingId && currentBookingId === targetBookingId) {
          return false;
        }
      }
      // Note: We always send to landlord when guest sends a message
      // The guest portal check was removed because it was blocking notifications to landlord
      return true;

    case 'guest_action':
      // Don't send if landlord is on dashboard
      if (currentPath === '/dashboard' || currentPath === '/dashboard/') {
        return false;
      }
      return true;

    default:
      return true;
  }
}

/**
 * A translator fixed to the RECIPIENT's language. Push texts are read by the
 * other side, so the sender's UI language is the wrong one. Only hr / en are
 * complete (other bundles are lazy and partial) → anything else gets English.
 */
export function pushT(language: string | null | undefined): (key: string, defaultValue: string) => string {
  const base = (language || '').split('-')[0].toLowerCase();
  const lng = base === 'hr' || base === 'en' ? base : 'en';
  const fixed = typeof i18n.getFixedT === 'function' ? i18n.getFixedT(lng) : null;
  return (key, defaultValue) => {
    const text = fixed ? fixed(key, { defaultValue }) : i18n.t(key, defaultValue);
    return typeof text === 'string' && text ? text : defaultValue;
  };
}

/** One-line push preview of a chat message (plain text, capped). */
function pushPreview(text: string | null | undefined, max = 80): string {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/**
 * Notify landlord about a new message from guest.
 *
 * Whether the host is looking at that chat right now is decided by the server
 * (send-push asks should_send_push) — this runs in the GUEST's browser, where
 * the old page-visibility check was always "send".
 */
export async function notifyLandlordNewMessage(params: {
  landlordId: string;
  bookingId: string;
  guestName: string;
  messagePreview?: string;
  /** The message carries a photo (and maybe no text). */
  hasImage?: boolean;
  /** The host's language (push texts are in the reader's language). */
  hostLanguage?: string | null;
}): Promise<void> {
  const { landlordId, bookingId, guestName, messagePreview, hasImage, hostLanguage } = params;
  const t = pushT(hostLanguage ?? 'hr');
  const preview = pushPreview(messagePreview);

  await sendPushNotification({
    userId: landlordId,
    userType: 'landlord',
    bookingId,
    title: `💬 ${guestName || t('chat.guest', 'Guest')}`,
    body: preview || (hasImage ? `📷 ${t('chat.photo', 'Photo')}` : t('notifications.push.newMessage', 'New message')),
    url: `/dashboard/messages?booking=${bookingId}`,
    tag: `message-${bookingId}`,
    notificationType: 'new_message',
  });
}

/**
 * The token the guest's portal link should use for this booking: the group's
 * link when the booking is part of a (not trashed) group, else the booking's
 * own. Called from the landlord side (RLS lets the landlord read both rows).
 * Any failure falls back to the booking token.
 */
async function guestPortalTokenFor(bookingId: string, accessToken: string): Promise<string> {
  try {
    const supabase = getSupabase();
    const { data: booking } = await supabase
      .from('bookings')
      .select('group_id')
      .eq('id', bookingId)
      .maybeSingle();
    const groupId = (booking as { group_id?: string | null } | null)?.group_id;
    if (!groupId) return accessToken;
    const { data: group } = await supabase
      .from('booking_groups')
      .select('access_token')
      .eq('id', groupId)
      .is('deleted_at', null)
      .maybeSingle();
    return (group as { access_token?: string | null } | null)?.access_token || accessToken;
  } catch {
    return accessToken;
  }
}

/**
 * Notify guest about a new message from landlord.
 *
 * Title and fallback body are in the GUEST's language (`guestLanguage`; hr / en,
 * anything else → English) — they used to be in the host's dashboard language.
 * `messagePreview` should already be the text the guest will read (the
 * translation when there is one); the caller decides.
 */
export async function notifyGuestNewMessage(params: {
  bookingId: string;
  accessToken: string;
  messagePreview?: string;
  guestLanguage?: string | null;
  /** The message carries a photo (and maybe no text). */
  hasImage?: boolean;
}): Promise<void> {
  const { bookingId, accessToken, messagePreview, guestLanguage, hasImage } = params;
  const t = pushT(guestLanguage);
  // A member of a group booking reads its chat inside the GROUP portal — the
  // member link opened the single-unit view and replaced the group link the
  // installed PWA had saved. The push still goes to the member token (the
  // guest's push identity); only the link changes.
  const portalToken = await guestPortalTokenFor(bookingId, accessToken);
  const preview = pushPreview(messagePreview);

  await sendPushNotification({
    userId: accessToken,
    userType: 'guest',
    bookingId,
    title: `💬 ${t('notifications.push.newMessageFromHost', 'New message from host')}`,
    body: preview || (hasImage ? `📷 ${t('chat.photo', 'Photo')}` : t('notifications.push.hostSentMessage', 'Your host sent you a message')),
    url: `/guest/portal/${portalToken}?tab=chat`,
    tag: `message-${bookingId}`,
    notificationType: 'new_message',
  });
}

/**
 * Notify landlord about guest action (departed, at_parking, arrived, location_shared)
 */
export async function notifyLandlordGuestAction(params: {
  landlordId: string;
  bookingId: string;
  guestName: string;
  actionType: 'departed' | 'at_parking' | 'arrived' | 'location_shared';
  etaMinutes?: number;
}): Promise<void> {
  const { landlordId, bookingId, guestName, actionType, etaMinutes } = params;

  // Check if we should send locally
  if (!shouldSendPushLocally('guest_action')) {
    if (import.meta.env.DEV) console.log('Skipping push - landlord is on dashboard');
    return;
  }

  let title: string;
  let body: string;

  switch (actionType) {
    case 'departed':
      title = `🚗 ${guestName} ${i18n.t('notifications.push.departed', 'has departed')}`;
      body = etaMinutes
        ? i18n.t('notifications.push.etaMinutes', 'Expected arrival in {{minutes}} minutes', { minutes: etaMinutes })
        : i18n.t('notifications.push.onTheWay', 'On the way to accommodation');
      break;
    case 'at_parking':
      title = `🅿️ ${guestName} ${i18n.t('notifications.push.atParking', 'is at the parking')}`;
      body = i18n.t('notifications.push.guestAtParking', 'Guest arrived at parking');
      break;
    case 'arrived':
      title = `🏠 ${guestName} ${i18n.t('notifications.push.arrived', 'has arrived')}`;
      body = i18n.t('notifications.push.guestArrived', 'Guest has arrived at destination');
      break;
    case 'location_shared':
      title = `📍 ${guestName} ${i18n.t('notifications.push.sharedLocation', 'shared location')}`;
      body = i18n.t('notifications.push.clickToView', 'Click to view on map');
      break;
  }

  await sendPushNotification({
    userId: landlordId,
    userType: 'landlord',
    bookingId,
    title,
    body,
    url: `/dashboard/bookings/${bookingId}`,
    tag: `action-${bookingId}-${actionType}`,
    notificationType: 'guest_action',
  });
}

/**
 * Notify landlord when admin responds to their support ticket
 */
export async function notifyLandlordTicketResponse(params: {
  landlordId: string;
  ticketId: string;
  messagePreview?: string;
}): Promise<void> {
  const { landlordId, ticketId, messagePreview } = params;

  await sendPushNotification({
    userId: landlordId,
    userType: 'landlord',
    title: `🛟 ${i18n.t('notifications.push.supportResponse', 'Support team response')}`,
    body: messagePreview || i18n.t('notifications.push.ticketResponse', 'You received a response to your ticket'),
    url: `/dashboard/support`,
    tag: `ticket-response-${ticketId}`,
    notificationType: 'support_reply',
  });
}

/**
 * Notify landlord about new guest feedback/review
 */
export async function notifyLandlordNewFeedback(params: {
  landlordId: string;
  bookingId: string;
  guestName: string;
  rating: number;
}): Promise<void> {
  const { landlordId, bookingId, guestName, rating } = params;

  const stars = '⭐'.repeat(rating);

  await sendPushNotification({
    userId: landlordId,
    userType: 'landlord',
    bookingId,
    title: `${stars} ${i18n.t('notifications.push.newReview', 'New review')}`,
    body: i18n.t('notifications.push.guestLeftRating', '{{name}} left a {{rating}}/5 rating', { name: guestName, rating }),
    url: `/dashboard/reviews`,
    tag: `feedback-${bookingId}`,
    notificationType: 'guest_action',
  });
}

/**
 * Update user activity (heartbeat)
 * Called periodically to let server know user is active
 */
export async function updateUserActivity(params: {
  userId: string;
  userType: 'landlord' | 'guest';
  currentPage: string;
  currentBookingId?: string;
  isOnline?: boolean;
}): Promise<void> {
  const { userId, userType, currentPage, currentBookingId, isOnline = true } = params;

  try {
    await getSupabase().rpc('update_user_activity', {
      p_user_id: userId,
      p_user_type: userType,
      p_current_page: currentPage,
      p_current_booking_id: currentBookingId || null,
      p_is_online: isOnline,
    });
  } catch (err) {
    // Silently fail - activity tracking is not critical
    if (import.meta.env.DEV) console.debug('Failed to update user activity:', err);
  }
}

export default {
  sendPushNotification,
  shouldSendPushLocally,
  notifyLandlordNewMessage,
  notifyGuestNewMessage,
  notifyLandlordGuestAction,
  notifyLandlordTicketResponse,
  updateUserActivity,
};
